import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { iepaProd } from "../../constants/global";

const Adapter = () => {
  // ---------------- STATES ----------------
  const [importExport, setImportExport] = useState("");
  const [name, setName] = useState("");
  const [seededCustom, setSeededCustom] = useState("custom");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [active, setActive] = useState(true);

  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [targetApp, setTargetApp] = useState("");
  const [mappings, setMappings] = useState([]);

  const [compileLoading, setCompileLoading] = useState(false);
  const [compileSuccess, setCompileSuccess] = useState(false);
  const [compileError, setCompileError] = useState("");

  const [sources, setSources] = useState([]);
  const [targetApps, setTargetApps] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [saveHeader, setSaveHeader] = useState(true);

  const importExportOptions = ["Import", "Export"];

  // ================= FETCH SOURCES =================
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const res = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/header/org/1`
        );
        const data = await res.json();
        setSources(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch sources", err);
      }
    };
    fetchSources();
  }, []);

  // ================= FETCH TARGET APPS =================
  useEffect(() => {
    if (!sourceId) {
      setTargetApps([]);
      return;
    }

    const fetchTargets = async () => {
      try {
        const res = await fetch(
          `${iepaProd.iepaAdapterSrvAPI}/adapters/target/active/org/1`
        );
        const data = await res.json();
        setTargetApps(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch targets", err);
      }
    };

    fetchTargets();
  }, [sourceId]);

  // ================= FETCH SOURCE HEADERS =================
  const fetchSourceHeaders = async (id) => {
    try {
      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/header/${id}/org/1`
      );
      console.log('res', res)
      const data = await res.json();
      console.log('data', data)
      const headers = Array.isArray(data?.adapterMappings)
        ? data.adapterMappings.map(item => item.sourceName)
        : [];
      console.log('headers', headers)
      setSourceHeaders(headers);
    } catch (err) {
      console.error("Failed to fetch source headers", err);
      setSourceHeaders([]);
    }
  };

  // ================= FETCH TARGET FIELDS =================
  const fetchTargetFields = async (targetName) => {
    try {
      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/target/fields/${targetName}`
      );
      const data = await res.json();

      const rows = data.map((f, index) => ({
        srNo: index + 1,
        targetField: f.fieldName || f,
        sourceField: "",
        rule: "",
        type: "Mandatory",
      }));

      setMappings(rows);
    } catch (err) {
      console.error("Failed to fetch target fields", err);
      setMappings([]);
    }
  };

  // ================= MAPPING CHANGE =================
  const handleMappingChange = (index, key, value) => {
    const updated = [...mappings];
    updated[index][key] = value;
    setMappings(updated);
  };

  // ================= COMPILE =================
  const handleCompile = () => {
    setCompileError("");
    setCompileSuccess(false);

    const invalid = mappings.filter(
      (m) => m.type === "Mandatory" && !m.sourceField
    );

    if (invalid.length > 0) {
      setCompileError(
        `Validation failed: ${invalid.length} mandatory field(s) not mapped`
      );
      return;
    }

    setCompileLoading(true);

    setTimeout(() => {
      setCompileLoading(false);
      setCompileSuccess(true);
    }, 2000);
  };


  const handleSave = async () => {
    try {
      const now = new Date().toISOString();

      // Build adapterMappings from UI mappings table
      const adapterMappingsPayload = mappings.map((m, index) => ({
        id: 0,
        createdBy: 1,
        updatedBy: 1,
        createDate: now,
        updateDate: now,
        orgId: 1,
        name: `${targetApp}_${m.targetField}`,
        sourceName: m.sourceField,
        destinationName: m.targetField,
        inputType: "STRING",
        format: m.rule || "",
        status: "ACTIVE",
        adapter: targetApp,

        adapterRules: m.rule
          ? [
            {
              id: 0,
              createdBy: 1,
              updatedBy: 1,
              createDate: now,
              updateDate: now,
              orgId: "1",
              name: "RULE_1",
              type: "TRANSFORM",
              attributeOperand: m.targetField,
              attributeOperator: "=",
              attributeValue: m.rule,
              adapterMapping: ""
            }
          ]
          : [],

        adapterMappingHeader: {
          id: 0,
          createdBy: 1,
          updatedBy: 1,
          createDate: now,
          updateDate: now,
          orgId: "1",
          sourceName: sourceId,
          destinationName: targetApp,
          status: "ACTIVE",
          uploadFileName: name,
          adapterMappings: []
        }
      }));

      // Final request body
      const requestBody = {
        id: 0,
        createdBy: 1,
        updatedBy: 1,
        createDate: now,
        updateDate: now,
        orgId: "1",
        name: name,
        description: seededCustom,
        location: "UI",
        maxRecords: 100000,
        reversibleFlag: saveHeader,
        startDate: startDate ? new Date(startDate).toISOString() : now,
        endDate: endDate ? new Date(endDate).toISOString() : now,
        status: active ? "ACTIVE" : "INACTIVE",
        adapterMappings: adapterMappingsPayload
      };

      console.log("REQUEST BODY 👉", requestBody);

      const res = await fetch(
        `${iepaProd.iepaAdapterSrvAPI}/adapters/mapping/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const data = await res.json();
      console.log("SAVE SUCCESS", data);
      alert("Saved Successfully");
    } catch (err) {
      console.error("SAVE ERROR", err);
      alert("Save Failed: " + err.message);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* HEADER */}
      <Grid container alignItems="center" mb={3}>
        <Grid item xs={6}>
          <Typography variant="h4">Adapter</Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Import / Export</InputLabel>
            <Select
              value={importExport}
              label="Import / Export"
              onChange={(e) => setImportExport(e.target.value)}
            >
              {importExportOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {importExport === "Import" && (
        <>
          {/* MASTER DETAILS */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <RadioGroup
                  row
                  value={seededCustom}
                  onChange={(e) => setSeededCustom(e.target.value)}
                >
                  <FormControlLabel value="seeded" control={<Radio />} label="Seeded" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                </RadioGroup>
              </Grid>

              <Grid item xs={3}>
                <TextField
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={3}>
                <TextField
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>

              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(e) => setActive(e.target.checked)}
                      color="success"                    
                    />
                  }
                  label="Active"
                />

              </Grid>
              <Grid item xs={3}>
                {/* Fudge / Save Header Checkbox */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveHeader}
                      onChange={(e) => setSaveHeader(e.target.checked)}
                      sx={{
                        padding: "6px",

                        /* default icon */
                        "& .MuiSvgIcon-root": {
                          borderRadius: "6px",
                        },

                        /* hover (unchecked) */
                        "&:hover .MuiSvgIcon-root": {
                          backgroundColor: "#e3f2fd",
                        },

                        /* checked */
                        "&.Mui-checked": {
                          color: "#1976d2",
                        },
                        "&.Mui-checked .MuiSvgIcon-root": {
                          backgroundColor: "#bbdefb",
                        },

                        /* hover when checked */
                        "&.Mui-checked:hover .MuiSvgIcon-root": {
                          backgroundColor: "#90caf9",
                        },
                      }}
                    />
                  }
                  label="Purge Interface"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      marginLeft: "8px",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* SOURCE + TARGET */}
          <Paper sx={{ p: 4, mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Source Name</InputLabel>
                  <Select
                    value={sourceId}
                    label="Source Name"
                    onChange={(e) => {
                      const id = e.target.value;
                      setSourceId(id);
                      setTargetApp("");
                      setSourceHeaders([]);
                      setMappings([]);
                      fetchSourceHeaders(id);
                    }}
                  >
                    {sources.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.uploadFileName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Target Application</InputLabel>
                  <Select
                    value={targetApp}
                    label="Target Application"
                    onChange={(e) => {
                      setTargetApp(e.target.value);
                      fetchTargetFields(e.target.value);
                    }}
                  >
                    {targetApps.map((t) => (
                      <MenuItem key={t.module} value={t.module}>
                        {t.module}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* SOURCE HEADERS GRID */}
          {sourceHeaders.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Source File Fields</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sr No</TableCell>
                      <TableCell>Field Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sourceHeaders.map((h, i) => (
                      <TableRow key={i}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{h}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          )}

          {/* MAPPING SECTION */}
          {targetApp && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Mapping Section</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {compileError && <Alert severity="error">{compileError}</Alert>}
                {compileSuccess && <Alert severity="success">Compiled Successfully</Alert>}
                {compileLoading && <LinearProgress />}

                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sr No</TableCell>
                      <TableCell>Target Field</TableCell>
                      <TableCell>Source Field</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.srNo}</TableCell>
                        <TableCell>{row.targetField}</TableCell>

                        <TableCell>
                          <Select
                            fullWidth
                            value={row.sourceField}
                            onChange={(e) =>
                              handleMappingChange(idx, "sourceField", e.target.value)
                            }
                          >
                            {sourceHeaders.map((h) => (
                              <MenuItem key={h} value={h}>
                                {h}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>

                        <TableCell>
                          <TextField
                            fullWidth
                            value={row.rule}
                            onChange={(e) =>
                              handleMappingChange(idx, "rule", e.target.value)
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <Select
                            fullWidth
                            value={row.type}
                            onChange={(e) =>
                              handleMappingChange(idx, "type", e.target.value)
                            }
                          >
                            <MenuItem value="Mandatory">Mandatory</MenuItem>
                            <MenuItem value="Optional">Optional</MenuItem>
                            <MenuItem value="Derived">Derived</MenuItem>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* ACTION BUTTONS */}
                <Box mt={4} display="flex" justifyContent="space-between">
                  <Box display="flex" gap={2}>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                    <Button variant="outlined">Cancel</Button>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Button variant="contained" onClick={handleCompile}>
                      Compile
                    </Button>
                    <Button variant="contained">Freeze</Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};

export default Adapter;
